import React, { useMemo } from "react";
import { MockedAuthManager } from "../../../contexts/AuthManager";
import { buildUser, buildWorkerProfile } from "../../factories";
import PostOnbaridngProfileEditorScreen from "../../../screens/profile-editor/PostOnboardingProfileEditor";
import {
    BankAccount,
    DocumentType,
    Gender,
    MaritalStatus,
    Person,
    ProfileType,
} from "../../../__generated__/graphql";
import type { Meta, StoryObj } from "@storybook/react";
import { WorkingPermit } from "../../../__generated__/graphql";
import { createStackNavigator } from "@react-navigation/stack";
import CivilStateEditorScreen from "../../../screens/profile-editor/CivilStateEditor";
import FiscalAttestationEditorScreen from "../../../screens/profile-editor/FiscalAttestationEditor";
import ChildrenEditorScreen from "../../../screens/profile-editor/ChildrenEditor";
import BankAccountEditorScreen from "../../../screens/profile-editor/BankAccountEditor";
import { MockedProvider } from "@apollo/client/testing";
import { MockedProfileSwitcherManager } from "../../../contexts/ProfileSwitcherManager";

const Stack = createStackNavigator<{
    PostOnboardingProfileEditor: undefined;
    CivilStateEdit: undefined;
    FiscalAttestationEdit: undefined;
    ChildrenEdit: undefined;
    BankAccountEdit: undefined;
}>();

type StoryComponentProps = {
    nationality?: string;
    maritalStatus?: MaritalStatus;
    hasOtherActivity?: boolean;
    workPermit?: WorkingPermit | null;
    partner?: Person;
    children?: Person[];
    hasFiscalAttestation?: boolean;
    bankAccount?: BankAccount;
    avsNumber?: string | null;
};

const StoryComponent = ({
    nationality,
    maritalStatus,
    hasOtherActivity,
    workPermit,
    partner,
    children,
    hasFiscalAttestation,
    bankAccount,
    avsNumber,
}: StoryComponentProps) => {
    const user = useMemo(
        () =>
            buildUser({
                nationality,
                workPermit,
                workerProfile: buildWorkerProfile({
                    avsNumber,
                    bankAccount,
                    partner,
                    maritalStatus: maritalStatus || (partner ? MaritalStatus.Married : undefined),
                    hasOtherActivity,
                    children,
                    hasChildren: Array.isArray(children) ? children.length > 0 : undefined,
                    hasFiscalAttestation,
                    documents: hasFiscalAttestation
                        ? [
                              {
                                  id: "document",
                                  type: DocumentType.FiscalAttestation,
                                  title: "Attestation.pdf",
                                  documentUrl: "",
                              },
                          ]
                        : undefined,
                }),
            }),
        [
            nationality,
            workPermit,
            avsNumber,
            bankAccount,
            partner,
            maritalStatus,
            hasOtherActivity,
            children,
            hasFiscalAttestation,
        ],
    );

    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={ProfileType.Worker}>
                <MockedProvider mocks={[]}>
                    <Stack.Navigator initialRouteName="PostOnboardingProfileEditor">
                        <Stack.Screen
                            name="PostOnboardingProfileEditor"
                            component={PostOnbaridngProfileEditorScreen}
                        />
                        <Stack.Screen name="CivilStateEdit" component={CivilStateEditorScreen} />
                        <Stack.Screen
                            name="FiscalAttestationEdit"
                            component={FiscalAttestationEditorScreen}
                        />
                        <Stack.Screen name="ChildrenEdit" component={ChildrenEditorScreen} />
                        <Stack.Screen name="BankAccountEdit" component={BankAccountEditorScreen} />
                    </Stack.Navigator>
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};

const meta = {
    title: "Screens/ProfileEditor/PostOnbardingProfileEditor",
    component: StoryComponent,
    argTypes: {
        workPermit: {
            options: Object.values(WorkingPermit),
            control: { type: "radio" },
            defaultValue: WorkingPermit.PermitB,
        },
        children: {
            control: { type: "object" },
        },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

const children: Person[] = [
    {
        id: "children1",
        firstName: "Kylian",
        lastName: "Mbappé",
        avsNumber: "756.1234.5678.97",
        birthdate: "2020-01-01",
        gender: Gender.Male,
    },
];

const partner: Person = {
    id: "partner",
    firstName: "Mona",
    lastName: "Lisa",
    avsNumber: "756.1234.5678.97",
    birthdate: "1503-05-12",
    gender: Gender.Female,
    monthlySalary: 0,
};

const defaultBankAccount = {
    id: "bankAccount",
    name: "My bank",
    iban: "CH9789144829733648596",
};

export const Empty: Story = {};

export const SwissWithoutChildren: Story = {
    args: {
        nationality: "CH",
    },
};

export const BPermitWithPartnerAndChildren: Story = {
    args: {
        workPermit: WorkingPermit.PermitB,
        children,
        partner,
        hasOtherActivity: false,
    },
};

export const CPermitWithPartner: Story = {
    args: {
        workPermit: WorkingPermit.PermitC,
        partner,
        hasOtherActivity: true,
    },
};

export const GPermitWithFiscalAttestation: Story = {
    args: {
        workPermit: WorkingPermit.PermitG,
        hasFiscalAttestation: true,
    },
};

export const SingleSwissComplete: Story = {
    args: {
        nationality: "CH",
        maritalStatus: MaritalStatus.Single,
        bankAccount: defaultBankAccount,
        hasOtherActivity: false,
    },
};

export const BPermitComplete: Story = {
    args: {
        workPermit: WorkingPermit.PermitB,
        children,
        maritalStatus: MaritalStatus.Single,
        hasOtherActivity: true,
        bankAccount: defaultBankAccount,
    },
};

export const FrenchWithoutPermit: Story = {
    args: {
        nationality: "FR",
        maritalStatus: MaritalStatus.Single,
        bankAccount: defaultBankAccount,
        hasOtherActivity: false,
        workPermit: null,
        avsNumber: null,
    },
};
